import { create } from "zustand";

/**
 * Selection is by track id and survives sort/filter for rows still present.
 * Select-all-filtered is a *filter reference* (scope + q), not 3,800 ids.
 */
export type SelectionMode =
  | { kind: "ids"; ids: Set<string>; anchorId: string | null; focusId: string | null }
  | {
      kind: "filter";
      scope: string;
      q: string;
      total: number;
      anchorId: string | null;
      focusId: string | null;
    };

interface SelectionState {
  mode: SelectionMode | null;
  clear: () => void;
  setFocus: (id: string | null) => void;
  /** Click: select only this row. */
  selectOnly: (id: string) => void;
  /** Ctrl/Cmd+Click: toggle this row. */
  toggle: (id: string) => void;
  /** Shift+Click: select range anchor→id over the visible ordered ids. */
  selectRange: (id: string, orderedIds: readonly string[]) => void;
  /** Move focus and collapse selection to it. */
  moveFocus: (id: string) => void;
  /** Extend selection from anchor through id. */
  extendTo: (id: string, orderedIds: readonly string[]) => void;
  selectAllFiltered: (scope: string, q: string, total: number) => void;
  isSelected: (id: string) => boolean;
  count: () => number;
  /** Concrete ids when mode is ids; null when mode is filter (server resolves). */
  selectedIds: () => string[] | null;
  bulkTarget: () =>
    | { entity_ids: string[] }
    | { scope: string; q?: string }
    | null;
}

function rangeIds(orderedIds: readonly string[], fromId: string, toId: string): string[] {
  const a = orderedIds.indexOf(fromId);
  const b = orderedIds.indexOf(toId);
  if (a < 0 || b < 0) return [toId];
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return orderedIds.slice(lo, hi + 1);
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  mode: null,

  clear: () => set({ mode: null }),

  setFocus: (id) =>
    set((state) => {
      if (!state.mode) return { mode: id ? { kind: "ids", ids: new Set(), anchorId: id, focusId: id } : null };
      return { mode: { ...state.mode, focusId: id } };
    }),

  selectOnly: (id) =>
    set({
      mode: { kind: "ids", ids: new Set([id]), anchorId: id, focusId: id },
    }),

  toggle: (id) =>
    set((state) => {
      if (state.mode?.kind === "filter") {
        // Drop to explicit ids starting empty then toggle — filter mode can't
        // subtract one row without resolving everything.
        return {
          mode: { kind: "ids", ids: new Set([id]), anchorId: id, focusId: id },
        };
      }
      const ids = new Set(state.mode?.kind === "ids" ? state.mode.ids : []);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      return {
        mode: ids.size === 0 ? null : { kind: "ids", ids, anchorId: id, focusId: id },
      };
    }),

  selectRange: (id, orderedIds) =>
    set((state) => {
      const anchor = state.mode?.anchorId ?? id;
      const ids = new Set(rangeIds(orderedIds, anchor, id));
      return {
        mode: { kind: "ids", ids, anchorId: anchor, focusId: id },
      };
    }),

  moveFocus: (id) =>
    set({
      mode: { kind: "ids", ids: new Set([id]), anchorId: id, focusId: id },
    }),

  extendTo: (id, orderedIds) =>
    set((state) => {
      const anchor = state.mode?.anchorId ?? id;
      const ids = new Set(rangeIds(orderedIds, anchor, id));
      return {
        mode: { kind: "ids", ids, anchorId: anchor, focusId: id },
      };
    }),

  selectAllFiltered: (scope, q, total) =>
    set({
      mode: {
        kind: "filter",
        scope,
        q,
        total,
        anchorId: null,
        focusId: null,
      },
    }),

  isSelected: (id) => {
    const mode = get().mode;
    if (!mode) return false;
    if (mode.kind === "filter") return true;
    return mode.ids.has(id);
  },

  count: () => {
    const mode = get().mode;
    if (!mode) return 0;
    if (mode.kind === "filter") return mode.total;
    return mode.ids.size;
  },

  selectedIds: () => {
    const mode = get().mode;
    if (!mode || mode.kind === "filter") return null;
    return [...mode.ids];
  },

  bulkTarget: () => {
    const mode = get().mode;
    if (!mode) return null;
    if (mode.kind === "filter") {
      return mode.q ? { scope: mode.scope, q: mode.q } : { scope: mode.scope };
    }
    return { entity_ids: [...mode.ids] };
  },
}));
